import { PaginationProps, usePagination } from '@/design-system/components/pagination/usePagination.hook';
import { t } from 'i18next';
import { useContext, useEffect, useMemo, useState } from 'react';

import { UserEarnActionListResponseDataItem, useUtxEarnSubscriptionsGetEarnActionList } from '@utx/utx-client';
import { LayoutContext } from 'contexts/LayoutContext';
import { ActivityHistoryFilterParams } from '../../../components/activityHistory/ActivityHistoryFilter';
import { getEarnCategoryTranslationKey } from '../../../utils/getEarnCategoryTranslationKey';
import { useStore } from 'store/store';

const PAGE_SIZE = 10;

interface UseSubscriptionHookParams {
  paginatedProps: PaginationProps;
}

export const useSubscription = ({ paginatedProps }: UseSubscriptionHookParams) => {
  const layout = useContext(LayoutContext);
  const store = useStore();

  const [filter, setFilter] = useState<ActivityHistoryFilterParams>({
    provider: undefined,
    product: undefined,
    search: undefined,
    action: undefined,
    fromDate: undefined,
    toDate: undefined
  });

  const [totalPages, setTotalPages] = useState(0);
  const [current, setCurrent] = useState(1);
  const [displayData, setDisplayData] = useState<UserEarnActionListResponseDataItem[]>([]);

  const pagination = usePagination({
    total: totalPages + 1,
    current: paginatedProps.current
  });

  function onNext() {
    if (current >= totalPages) return;
    setCurrent(current + 1);
  }

  function onPrev() {
    if (current <= 1) return;
    setCurrent(current - 1);
  }

  function onChangeFilter(values: ActivityHistoryFilterParams) {
    query.remove();
    setDisplayData([]);
    setCurrent(1);
    setFilter(values);
  }

  const query = useUtxEarnSubscriptionsGetEarnActionList(
    {
      ...filter,
      product: filter.product || '',
      fromDate: filter.fromDate?.toISOString() || '',
      toDate: filter.toDate?.toISOString() || '',
      pageSize: PAGE_SIZE,
      current
    },
    {
      query: {
        retry: false,
        onSuccess(data) {
          setDisplayData((prevData) => (layout.isMobile ? [...prevData, ...(data.data?.data || [])] : data.data?.data || []));
          return data;
        },
        keepPreviousData: true
      }
    }
  );

  useEffect(() => {
    if (query.data?.data?.total) {
      setTotalPages(Math.ceil(query.data.data.total / 10));
    }
  }, [query.data?.data]);

  useEffect(() => {
    query.remove();
    pagination.handlePageChange(1);
  }, [layout.isMobile]);

  const defaultFilter = [
    {
      label: t('earn-invest::glossary.all-categories'),
      value: ''
    }
  ];

  const categoriesFilters = defaultFilter.concat(
    store.offerCategories.map((category) => {
      const translatedCategory = getEarnCategoryTranslationKey(category);
      return {
        label: t(translatedCategory),
        value: category
      };
    })
  );

  return {
    filter,
    categoriesFilters,
    query,
    displayData,
    pagination,
    current,
    totalPages,
    onFilterChange: query.refetch,
    onChangeFilter,
    onNext,
    onPrev
  };
};
